import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { ArrowRight, Clock3, HeartHandshake, ShieldCheck } from 'lucide-react';
import {
	useGetMatchForm,
	useGetMyMatch,
	useSubmitMatchAnswer,
} from '@/services';
import { getUserData, removeUserData } from '@/utils/cookies';
import { buildFormAnswerPayload } from '@/utils/formAnswerPayload';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	clearMatchDraft,
	clearMatchReturnPending,
	readMatchDraft,
	saveMatchDraft,
} from '@/utils/matchHandoff';
import { MatchShell } from './components/MatchShell';
import { MatchQuestionStep } from './components/MatchQuestionStep';
import { MatchInterstitial } from './components/MatchInterstitial';
import { MatchAuthStep } from './components/MatchAuthStep';
import { MatchReveal } from './components/MatchReveal';
import { MatchResults } from './components/MatchResults';
import { buildMatchFlow, isQuestionAnswered } from './matchFlow';

type Stage =
	| 'boot'
	| 'intro'
	| 'flow'
	| 'auth'
	| 'reveal'
	| 'results'
	| 'unavailable';

type Answers = Record<string, string | null>;

/** Entrada de cada tela (sem animação de saída: AnimatePresence em modo "wait" travava entre passos). */
const STEP_VARIANTS = {
	enter: (direction: number) => ({ opacity: 0, x: direction * 40 }),
	center: { opacity: 1, x: 0 },
};

/**
 * /match — onboarding público de match.
 * Visitante responde sem login (rascunho local) → entra/cria conta → respostas são salvas
 * (1x por conta) → animação → 3 terapeutas → agendamento padrão.
 * Ranking real por escala: ver backend `match-ranking.helper.ts` (TODO).
 */
const MatchPage = () => {
	const [isLogged, setIsLogged] = useState<boolean>(() =>
		Boolean(getUserData()),
	);
	const [stage, setStage] = useState<Stage>('boot');
	const [answers, setAnswers] = useState<Answers>({});
	const [stepIndex, setStepIndex] = useState(0);
	const [direction, setDirection] = useState(1);
	const [result, setResult] = useState<Match.Result | null>(null);
	const [alreadyAnswered, setAlreadyAnswered] = useState(false);
	const [submitReady, setSubmitReady] = useState(false);
	const bootstrappedRef = useRef(false);
	/** Sempre o mapa de respostas mais recente (evita closure obsoleta ao finalizar logo após responder). */
	const answersRef = useRef<Answers>({});
	const autoSubmittedRef = useRef(false);

	const {
		data: form,
		isLoading: formLoading,
		isError: formError,
	} = useGetMatchForm();

	const {
		data: mine,
		isLoading: mineLoading,
		error: mineError,
	} = useGetMyMatch({ enabled: isLogged, retry: false });

	const { mutateAsync: submitAnswer } = useSubmitMatchAnswer();

	const questions = useMemo(() => form?.form_question ?? [], [form]);
	const { steps, activeQuestions } = useMemo(
		() => buildMatchFlow(questions, answers),
		[questions, answers],
	);
	const answeredCount = activeQuestions.filter((q) =>
		isQuestionAnswered(q, answers),
	).length;

	/** Sessão expirada com cookie antigo: trata como visitante (o interceptor não redireciona em /match). */
	useEffect(() => {
		if (!mineError) return;
		if (isAxiosError(mineError) && mineError.response?.status === 401) {
			removeUserData();
			setIsLogged(false);
			return;
		}
		if (isAxiosError(mineError) && mineError.response?.status === 403) {
			// Sessão de outro perfil (profissional/admin): segue como visitante para o match.
			setIsLogged(false);
		}
	}, [mineError]);

	const finishReveal = useCallback(() => setStage('results'), []);

	const handleSubmit = useCallback(
		async (sourceAnswers: Answers) => {
			if (!form) return;
			setSubmitReady(false);
			setStage('reveal');
			const payload = buildFormAnswerPayload(
				form.form_question,
				sourceAnswers,
			);
			try {
				const res = await submitAnswer({ answer: payload });
				clearMatchDraft();
				clearMatchReturnPending();
				setResult(res);
				setAlreadyAnswered(false);
				setSubmitReady(true);
			} catch (error) {
				if (isAxiosError(error) && error.response?.status === 409) {
					// Já respondeu nesta conta: o GET /match/me traz o resultado.
					clearMatchDraft();
					setIsLogged(true);
					bootstrappedRef.current = false;
					setStage('boot');
					return;
				}
				if (isAxiosError(error) && error.response?.status === 401) {
					removeUserData();
					setIsLogged(false);
					setStage('auth');
					toast.info(
						'Sua sessão expirou. Entre novamente para ver seu match.',
					);
					return;
				}
				toast.error(
					getErrorMessage(
						error,
						'Não foi possível enviar suas respostas.',
					),
				);
				setStage(isLogged ? 'flow' : 'auth');
			}
		},
		[form, submitAnswer, isLogged],
	);

	/** Bootstrap: decide a tela inicial quando form (e /match/me, se logado) carregarem. */
	useEffect(() => {
		if (bootstrappedRef.current) return;
		if (formLoading) return;
		if (formError || !form) {
			bootstrappedRef.current = true;
			setStage('unavailable');
			return;
		}
		if (isLogged && mineLoading) return;

		bootstrappedRef.current = true;

		const draft = readMatchDraft();
		const draftAnswers =
			draft && draft.form_id === form.id ? draft.answers : {};
		const draftCompleted = Boolean(
			draft && draft.form_id === form.id && draft.completed,
		);

		if (isLogged && mine?.answered && mine.result) {
			clearMatchDraft();
			clearMatchReturnPending();
			setResult(mine.result);
			setAlreadyAnswered(true);
			setStage('results');
			return;
		}

		answersRef.current = draftAnswers;
		setAnswers(draftAnswers);

		if (isLogged && draftCompleted && !autoSubmittedRef.current) {
			// Voltou do login/cadastro com o questionário pronto: envia direto.
			autoSubmittedRef.current = true;
			void handleSubmit(draftAnswers);
			return;
		}

		if (Object.keys(draftAnswers).length > 0) {
			const { steps: draftSteps } = buildMatchFlow(
				form.form_question,
				draftAnswers,
			);
			const firstUnanswered = draftSteps.findIndex(
				(s) =>
					s.kind === 'question' &&
					!isQuestionAnswered(s.question, draftAnswers),
			);
			setStepIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
			setStage(draftCompleted && !isLogged ? 'auth' : 'flow');
			return;
		}

		setStage('intro');
	}, [
		form,
		formLoading,
		formError,
		isLogged,
		mine,
		mineLoading,
		handleSubmit,
	]);

	const persistDraft = useCallback(
		(nextAnswers: Answers, completed: boolean) => {
			if (!form) return;
			saveMatchDraft({
				form_id: form.id,
				answers: nextAnswers,
				completed,
			});
		},
		[form],
	);

	const handleAnswerChange = (
		question: Forms.FormQuestion,
		value: string,
	) => {
		const next = {
			...answersRef.current,
			[String(question.number)]: value,
		};
		answersRef.current = next;
		persistDraft(next, false);
		setAnswers(next);
	};

	const goNext = () => {
		setDirection(1);
		const nextIndex = stepIndex + 1;
		// Recalcula com as respostas mais recentes: a última resposta pode revelar uma pergunta condicional.
		const { steps: latestSteps } = buildMatchFlow(
			questions,
			answersRef.current,
		);
		if (nextIndex >= latestSteps.length) {
			finishQuestionnaire();
			return;
		}
		setStepIndex(nextIndex);
	};

	const goBack = () => {
		if (stepIndex === 0) {
			setStage('intro');
			return;
		}
		setDirection(-1);
		setStepIndex(stepIndex - 1);
	};

	const finishQuestionnaire = () => {
		const latest = answersRef.current;
		persistDraft(latest, true);
		if (isLogged) {
			void handleSubmit(latest);
			return;
		}
		setStage('auth');
	};

	const handleAuthenticated = () => {
		setIsLogged(true);
		void handleSubmit(answersRef.current);
	};

	const currentStep =
		steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];
	const totalQuestions = activeQuestions.length;
	const questionPosition =
		currentStep?.kind === 'question' ? currentStep.position : answeredCount;
	const progress =
		totalQuestions > 0 ? Math.min(1, questionPosition / totalQuestions) : 0;
	const isLastQuestion =
		currentStep?.kind === 'question' &&
		currentStep.position === totalQuestions &&
		stepIndex === steps.length - 1;

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [stepIndex, stage]);

	const renderStage = () => {
		switch (stage) {
			case 'boot':
				return (
					<div className="flex flex-1 items-center justify-center py-20">
						<motion.span
							className="h-10 w-10 rounded-full border-4 border-stone-200 border-t-brand-pink"
							animate={{ rotate: 360 }}
							transition={{
								duration: 0.9,
								repeat: Infinity,
								ease: 'linear',
							}}
						/>
					</div>
				);
			case 'unavailable':
				return (
					<div className="flex flex-1 flex-col items-center justify-center gap-4 py-20 text-center">
						<h1 className="text-2xl font-black tracking-tight text-stone-900">
							O match ainda não está disponível.
						</h1>
						<p className="max-w-md text-sm font-medium text-stone-500">
							Estamos preparando o questionário. Enquanto isso,
							você pode conhecer todos os profissionais e agendar
							normalmente.
						</p>
						<a
							href="/agendamento"
							className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-stone-800"
						>
							Ver profissionais
							<ArrowRight className="h-4 w-4" />
						</a>
					</div>
				);
			case 'intro':
				return (
					<div className="flex flex-1 flex-col justify-center gap-8 py-6">
						<div className="space-y-4">
							<p className="text-[11px] font-black uppercase tracking-[0.2em] text-brand-pink">
								Match Plumaa
							</p>
							<h1 className="text-3xl font-black leading-[1.05] tracking-tight text-stone-900 md:text-5xl">
								Vamos encontrar o terapeuta certo para você.
							</h1>
							<p className="max-w-xl text-base font-medium text-stone-500 md:text-lg">
								Algumas perguntas rápidas sobre o que você está
								vivendo e como prefere ser acompanhado. No
								final, mostramos os 3 profissionais com mais
								afinidade com você.
							</p>
						</div>
						<ul className="grid gap-3 sm:grid-cols-3">
							{[
								{
									icon: Clock3,
									text: `${totalQuestions} perguntas, cerca de 2 minutos`,
								},
								{
									icon: ShieldCheck,
									text: 'Suas respostas são confidenciais',
								},
								{
									icon: HeartHandshake,
									text: 'Sem compromisso: você escolhe com quem seguir',
								},
							].map(({ icon: Icon, text }) => (
								<li
									key={text}
									className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3"
								>
									<Icon className="h-4 w-4 shrink-0 text-brand-pink" />
									<span className="text-sm font-semibold text-stone-700">
										{text}
									</span>
								</li>
							))}
						</ul>
						<div>
							<button
								type="button"
								onClick={() => {
									setDirection(1);
									setStepIndex(0);
									setStage('flow');
								}}
								className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-stone-900/10 transition-all hover:bg-stone-800"
							>
								Começar
								<ArrowRight className="h-4 w-4" />
							</button>
						</div>
					</div>
				);
			case 'flow':
				if (!currentStep) return null;
				return (
					<motion.div
						key={
							currentStep.kind === 'question'
								? `q-${currentStep.question.id}`
								: `i-${currentStep.index}`
						}
						custom={direction}
						variants={STEP_VARIANTS}
						initial="enter"
						animate="center"
						transition={{ duration: 0.28, ease: 'easeOut' }}
						className="flex flex-1 flex-col"
					>
						{currentStep.kind === 'question' ? (
							<MatchQuestionStep
								question={currentStep.question}
								value={
									answers[String(currentStep.question.number)]
								}
								onChange={(v) =>
									handleAnswerChange(currentStep.question, v)
								}
								onNext={goNext}
								isLast={isLastQuestion}
							/>
						) : (
							<MatchInterstitial
								content={currentStep.content}
								answered={answeredCount}
								total={totalQuestions}
								onNext={goNext}
							/>
						)}
					</motion.div>
				);
			case 'auth':
				return (
					<MatchAuthStep
						onAuthenticated={handleAuthenticated}
						totalAnswered={answeredCount}
					/>
				);
			case 'reveal':
				return (
					<MatchReveal ready={submitReady} onDone={finishReveal} />
				);
			case 'results':
				return result ? (
					<MatchResults
						result={result}
						alreadyAnswered={alreadyAnswered}
					/>
				) : null;
			default:
				return null;
		}
	};

	const showProgress = stage === 'flow' || stage === 'auth';
	const stepLabel =
		stage === 'flow' && currentStep?.kind === 'question'
			? `Pergunta ${currentStep.position} de ${totalQuestions}`
			: stage === 'auth'
			  ? 'Último passo'
			  : undefined;

	return (
		<>
			<Helmet>
				<title>Match | Plumaa</title>
				<meta
					name="description"
					content="Responda algumas perguntas e descubra os terapeutas da Plumaa com mais afinidade com você."
				/>
			</Helmet>
			<MatchShell
				progress={
					showProgress ? (stage === 'auth' ? 1 : progress) : undefined
				}
				stepLabel={stepLabel}
				onBack={stage === 'flow' ? goBack : undefined}
				hideClose={stage === 'reveal'}
				wide={stage === 'results'}
			>
				{renderStage()}
			</MatchShell>
		</>
	);
};

export { MatchPage };
